import { useInfiniteQuery } from '@tanstack/react-query';
import { expertTipsApi } from '../services/expertTipsApi';

const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export const expertTipKeys = {
  all: ['expert-tips'] as const,
  list: () => [...expertTipKeys.all, 'list'] as const,
  infinite: (timezone: string) => [...expertTipKeys.all, 'infinite', timezone] as const,
};

/**
 * Hook to fetch published expert tips with "Load More" pagination.
 * Uses useInfiniteQuery — call fetchNextPage() on "Load More" button click.
 *
 * Usage:
 *   const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useExpertTips();
 *   const allTips = data?.pages.flatMap(page => page.tips) ?? [];
 */
export function useExpertTips(pageSize: number = 20) {
  const timezone = getTimezone();

  return useInfiniteQuery({
    queryKey: expertTipKeys.infinite(timezone),
    queryFn: ({ pageParam = 1 }) =>
      expertTipsApi.getExpertTips(pageParam as number, pageSize, timezone),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage ? lastPage.pagination.page + 1 : undefined;
    },
    initialPageParam: 1,
  });
}
